import { useMemo } from 'react';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { AvailableProductLine } from '../../../Models/Enums';
import { getPositionAndProductLineFromConfigurationKey } from '../../../Utils/UnitConfigurationHelper';

/**
 * Utility functions for PHEPlateTypeManagement component
 */

/**
 * Filter data based on search term
 */
export const useFilteredData = (currentData: IPHEPlateType[], searchTerm: string) => {
  return useMemo(() => {
    if (!searchTerm.trim()) {
      return currentData;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    return currentData.filter((item) => 
      (item.text && item.text.toLowerCase().includes(searchTermLower)) || 
      (item.key && item.key.toLowerCase().includes(searchTermLower))
    );
  }, [currentData, searchTerm]);
};

/**
 * Sort data based on current sort settings
 */
export const useSortedData = (
  filteredData: IPHEPlateType[], 
  order: 'asc' | 'desc', 
  orderBy: string
) => {
  return useMemo(() => {
    const dataToSort = [...filteredData];
    
    if (!orderBy) {
      return dataToSort;
    }
    
    return dataToSort.sort((a, b) => {
      const aValue = a[orderBy as keyof IPHEPlateType];
      const bValue = b[orderBy as keyof IPHEPlateType];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return order === 'asc' ? -1 : 1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? 1 : -1;
      
      // Handle boolean values
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return order === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      // Handle string values and other types
      const aString = String(aValue);
      const bString = String(bValue);
      
      return order === 'asc' 
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  }, [filteredData, order, orderBy]);
};

/**
 * Utility to get position information from configuration key
 */
export const getPositionInfo = (configKey: string) => {
  return getPositionAndProductLineFromConfigurationKey(configKey);
};

/**
 * Check if two position arrays are equal (for form validation)
 */
export const arePositionArraysEqual = (arr1: string[], arr2: string[]): boolean => {
  return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
};

/**
 * Get display name for a PHE type (fallback to key if no text)
 */
export const getPHETypeDisplayName = (item: IPHEPlateType): string => {
  return item.text || item.key || 'Unknown';
};

/**
 * Validate form fields
 */
export const validateFormFields = (
  pheTypeActive: IPHEPlateType | undefined,
  text: string,
  key: string,
  allowedPositionTexts: string[],
  availablePositions: string[]
): boolean => {
  // For new items, require text and key
  if (pheTypeActive === undefined) {
    return text.trim() !== '' && key.trim() !== '';
  }
  
  // For existing items, check if positions have changed
  return !arePositionArraysEqual(allowedPositionTexts, availablePositions);
};

/**
 * Format search results count
 */
export const formatSearchResultsCount = (filteredCount: number, totalCount: number): string => {
  if (filteredCount === totalCount) {
    return `Showing ${totalCount} PHE plate types`;
  }
  return `Showing ${filteredCount} of ${totalCount} PHE plate types`;
};

/**
 * Get available product line from checkboxes
 */
export const getAvailableProductLine = (industrial: boolean, hygienic: boolean): AvailableProductLine | undefined => {
  if (industrial) {
    return AvailableProductLine.Industrial;
  } else if (hygienic) {
    return AvailableProductLine.Hygienic;
  }
  return undefined;
};

/**
 * Check if PHE type has specific product line
 */
export const hasPHETypeProductLine = (item: IPHEPlateType, productLine: AvailableProductLine): boolean => {
  return item.availableProductLine === productLine;
};

/**
 * Sort table column helper
 */
export const getNextSortDirection = (currentOrderBy: string, property: string, currentOrder: 'asc' | 'desc'): 'asc' | 'desc' => {
  const isAsc = currentOrderBy === property && currentOrder === 'asc';
  return isAsc ? 'desc' : 'asc';
};

/**
 * Check if column is sortable
 */
export const isColumnSortable = (columns: any[], columnId: string): boolean => {
  const column = columns.find(col => col.id === columnId);
  return column ? column.sortable !== false : false;
}; 